/** The Facilities band's charts - ADR-0212, package P9.
 *
 * - **Coverage** (`CoverageGrid`): one row per expected source (a site's electricity, a site's gas),
 *   one column per month, from `GET /coverage`. **The six census states are never merged**: a row is
 *   one stream so its cell is that stream's own state, and nothing here averages a site's streams
 *   into one colour. A window spanning several months (a quarterly bill) is that state in each month
 *   it covers; a month no window covers is "not expected". The rows with the most missing come first
 *   and at most eight are drawn; the full census is Processing's Missing tab (the card's link). A
 *   cell opens its source.
 * - **Sites by emissions** (`HBarList`): the sites the organisation tree states a total for, ranked.
 *   A bar is stacked by scope, each part in its scope's colour (tonnes take their scope's colour), and
 *   opens the figures behind the site.
 */

import { HBarList, CoverageGrid } from "../../components/charts";
import type { BarSegment, HBarItem } from "../../components/charts";
import type { CoverageRollup } from "../../api/coverageRollup";
import type { OrgTree } from "../../api/vendorSummary";
import { EmptyState } from "../../components/ui";
import { tonnes } from "../../lib/format";
import { BandCard } from "./BandCard";
import type { BandRead } from "./BandCard";
import { geometryTonnes } from "./bandModel";
import { coverageGridOf } from "./gridModel";
import type { ReactNode } from "react";

export function CoverageCard({
  read,
  rollup,
  onOpenSource,
  more,
}: {
  read: BandRead;
  rollup: CoverageRollup | undefined;
  onOpenSource: (sourceId: string) => void;
  /** One quiet link to the whole census, built by the page. */
  more?: ReactNode;
}) {
  const model = rollup ? coverageGridOf(rollup) : undefined;
  return (
    <BandCard title="Bills received by month" unit="sources" reads={[read]} hasTable={false} {...(more ? { action: more } : {})} testId="band-coverage">
      {model && model.sites.length > 0 ? (
        <CoverageGrid
          sites={model.sites}
          months={model.months}
          caption="Bills received by site and month"
          onOpenReceipt={(source) => {
            onOpenSource(source);
          }}
          testId="band-coverage-grid"
        />
      ) : (
        <EmptyState title="No source is expected yet" compact />
      )}
    </BandCard>
  );
}

const SCOPE_COLOUR = { "1": "scope1", "2": "scope2", "3": "scope3" } as const;

/** A site's total as its scopes, in the scopes' colours: the tree's own per-scope figures, never a
 * share worked out here. A scope with nothing in this site is not a part. */
function segmentsOf(site: NonNullable<OrgTree>["facilities"][number]): BarSegment[] {
  return site.totals.by_scope.flatMap((cell, at) => {
    const scope = String(at + 1) as "1" | "2" | "3";
    const value = geometryTonnes(cell.kgco2e);
    return value > 0
      ? [{ key: scope, label: `Scope ${scope}`, colour: SCOPE_COLOUR[scope], value, text: `${tonnes(cell.kgco2e)} t` }]
      : [];
  });
}

export function SiteTotalsCard({
  read,
  tree,
  onOpen,
}: {
  read: BandRead;
  tree: OrgTree | undefined;
  /** Opens the figures behind a site; called with `site-<id>`. */
  onOpen: (key: string) => void;
}) {
  const items: HBarItem[] = [...(tree?.facilities ?? [])]
    .sort((a, b) => Number(b.totals.kgco2e) - Number(a.totals.kgco2e))
    .map((site) => ({
      key: site.facility_id,
      label: site.name,
      ...(site.retired ? { sub: "Retired" } : {}),
      value: Math.max(0, geometryTonnes(site.totals.kgco2e)),
      text: `${tonnes(site.totals.kgco2e)} t`,
      segments: segmentsOf(site),
      ...(site.totals.figures > 0 ? { receipt: `site-${site.facility_id}` } : { notAssessed: true }),
    }));
  return (
    <BandCard title="Sites by emissions" unit="tCO2e" reads={[read]} testId="band-site-totals">
      {items.length > 0 ? (
        <HBarList items={items} caption="Sites by tCO2e" unit="tCO2e" onOpenReceipt={onOpen} testId="band-site-totals-list" />
      ) : (
        <EmptyState title="No sites yet" compact />
      )}
    </BandCard>
  );
}
